import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SparkleIcon as Sparkle, DogIcon as Dog, SlidersHorizontalIcon as SlidersHorizontal } from "@phosphor-icons/react";
import { Indicium, WaxSeal } from "@/components/luxury";

/**
 * Dashboard widget — "Smart matches" pulls 4-8 recommended for-sale dogs
 * via the weighted-scoring matchmaker. Each match also surfaces a tiny
 * "why" micro-credit ("breed · verified · price · fresh") so the user
 * sees *what* the algorithm matched on — pure transparency / trust UX.
 *
 * Match shape from /api/recommendations/dashboard:
 *   { id, name, breed, sex, asking_price, photo, score, why,
 *     lifestyle, breeder_id, verified_kennel }
 *   where `why` is a partial dict of:
 *     { breed, price, sex, verified, pedigree, geo, fresh,
 *       energy, kid, apartment, grooming, climate }
 *   each value being the points contributed by that signal.
 */

// Render labels for the `why` keys. Order matters: shows up left-to-right
// in the credit line, with the strongest contributors first.
const WHY_LABELS = {
  breed: "breed",
  verified: "verified",
  price: "price fit",
  pedigree: "pedigree",
  sex: "sex",
  geo: "near you",
  fresh: "fresh",
  // Lifestyle dimensions (matched against /me/buyer-preferences).
  energy: "energy",
  kid: "kid-friendly",
  apartment: "fits your home",
  grooming: "grooming",
  climate: "climate",
};

function MatchCredit({ why }) {
  if (!why || Object.keys(why).length === 0) return null;
  // Sort signals by points descending, then take the top 3 so the
  // micro-line stays scannable.
  const top = Object.entries(why)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([k]) => WHY_LABELS[k])
    .filter(Boolean);
  if (top.length === 0) return null;
  return (
    <p
      className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A6A00] mt-1 truncate"
      data-testid="smart-match-why"
      title={`Matched on: ${top.join(", ")}`}
    >
      {top.join(" · ")}
    </p>
  );
}

export default function SmartMatches() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/recommendations/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || !data.matches?.length) return null;

  const hasLifestylePrefs = !!data.has_lifestyle_prefs;

  return (
    <section className="bg-white border border-[#EAEAEA] rounded-xl p-6 mb-8 shadow-luxury-light" data-testid="smart-matches-widget">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
          <Indicium icon={Sparkle} tone="gold" size="sm" />
          Dog matches for you
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {data.favored_breeds?.length > 0 && (
            <span className="text-xs text-[#6B6B6B]">Based on your interest in {data.favored_breeds.join(", ")}</span>
          )}
          {/* Always-present "Refine" link. The label changes from "Tell
              us what you want" → "Refine your matches" once the user
              has set at least one lifestyle preference, so the CTA reads
              right whether the user is cold-start or returning. */}
          <Link
            to="/me/buyer-preferences"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-gold-dark hover:text-crimson transition-colors"
            data-testid="smart-matches-refine-link"
          >
            <SlidersHorizontal size={14} weight="bold" />
            {hasLifestylePrefs ? "Refine your matches" : "Tell us what you want"}
          </Link>
        </div>
      </div>
      <div className="gold-hairline mb-4" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.matches.slice(0, 8).map((m) => (
          <Link
            key={m.id}
            to={`/dogs/${m.id}`}
            className="relative bg-cream/60 border border-[#EAEAEA] hover:border-gold/55 rounded-lg p-3 hover:-translate-y-0.5 hover:shadow-gold-glow transition-all duration-500"
            data-testid={`smart-match-${m.id}`}
          >
            {m.verified_kennel && (
              <span className="absolute top-2 right-2 z-10" title="Verified kennel">
                <WaxSeal size={20} />
              </span>
            )}
            <div className="aspect-[4/3] bg-white border border-[#EAEAEA] rounded mb-2 flex items-center justify-center overflow-hidden">
              {m.photo ? (
                <img src={`/api/storage/files/${m.photo}`} alt={m.name} className="w-full h-full object-cover photo-luxury" />
              ) : (
                <Dog size={28} weight="duotone" className="text-[#6B6B6B]/40" />
              )}
            </div>
            <div className="text-sm font-medium truncate">{m.name}</div>
            <div className="text-xs text-[#6B6B6B] truncate">{m.breed}</div>
            {m.asking_price > 0 && (
              <div className="text-sm font-bold text-gold mt-1">${Math.round(m.asking_price)}</div>
            )}
            <MatchCredit why={m.why} />
          </Link>
        ))}
      </div>
    </section>
  );
}
